import classNames from 'classnames';
import { Dispatch, SetStateAction, SyntheticEvent, useCallback } from 'react';
import { Form } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';

import Icon from 'components/icons/Icon';
import { IconSymbol, IconVariant } from 'components/icons/Icon.types';

import './DialogHeader.scss';

type DialogHeaderProps = {
  title: string | React.JSX.Element;
  itemsPerPage: number;
  setItemsPerPage: Dispatch<SetStateAction<number>>;
  showIds: boolean;
  setShowIds: Dispatch<SetStateAction<boolean>>;
  searchItem?: string;
  setSearchItem?: Dispatch<SetStateAction<string>>;
  allItems?: boolean;
  setAllItems?: Dispatch<SetStateAction<boolean>>;
  hideInactiveGuests?: boolean;
  setHideInactiveGuests?: Dispatch<SetStateAction<boolean>>;
  showCodes?: boolean;
  setShowCodes?: Dispatch<SetStateAction<boolean>>;
};
export function DialogHeader(props: DialogHeaderProps) {
  const {
    title,
    itemsPerPage,
    setItemsPerPage,
    showIds,
    setShowIds,
    searchItem,
    setSearchItem,
    allItems = false,
    setAllItems,
    hideInactiveGuests,
    setHideInactiveGuests,
    showCodes,
    setShowCodes,
  } = props;
  const intl = useIntl();

  const handleSearchChange = useCallback(
    (e: SyntheticEvent) => {
      const target = e.target as HTMLInputElement;
      setSearchItem && setSearchItem(target.value);
    },
    [setSearchItem],
  );

  const handleSearchClear = useCallback(() => {
    setSearchItem && setSearchItem('');
  }, [setSearchItem]);

  const handleItemsPerPageChange = useCallback(
    (e: SyntheticEvent) => {
      const target = e.target as HTMLInputElement;
      setItemsPerPage(parseInt(target.value));
    },
    [setItemsPerPage],
  );

  const handleAllItemsChange = useCallback(() => {
    setAllItems && setAllItems(!allItems);
  }, [setAllItems, allItems]);

  const handleHideInactiveGuestsChange = useCallback(() => {
    setHideInactiveGuests && setHideInactiveGuests(!hideInactiveGuests);
  }, [setHideInactiveGuests, hideInactiveGuests]);

  const handleShowCodesChange = useCallback(() => {
    setShowCodes && setShowCodes(!showCodes);
  }, [setShowCodes, showCodes]);

  const handleShowIdsChange = useCallback(() => {
    setShowIds(!showIds);
  }, [setShowIds, showIds]);

  return (
    <div className="dialog_header">
      <div>{title}</div>
      {setSearchItem && (
        <>
          <Form.Control
            className="search_item"
            type="text"
            name="searchItem"
            value={searchItem ?? ''}
            onChange={handleSearchChange}
            placeholder={intl.formatMessage({ id: 'X.SEARCH.ITEM' })}
          />
          <Icon
            className={classNames('search_close', {
              hidden: !searchItem,
            })}
            symbol={IconSymbol.close}
            hoverVariant={IconVariant.dark}
            size={18}
            onClick={handleSearchClear}
          />
        </>
      )}
      <div
        className={classNames('items_per_page_label', {
          disabled: !!allItems,
        })}
      >
        <FormattedMessage id="X.ITEMS_PER_PAGE" />:
      </div>
      <Form.Control
        className="items_per_page"
        type="number"
        name="itemsPerPage"
        value={itemsPerPage}
        onChange={handleItemsPerPageChange}
        required
        min={1}
        disabled={!!allItems}
      />
      {setAllItems && (
        <Form.Check
          className="checkbox"
          type="checkbox"
          label={intl.formatMessage({ id: 'X.SEARCH.ALL' })}
          id="allItems"
          name="allItems"
          checked={allItems}
          onChange={handleAllItemsChange}
        />
      )}
      {setHideInactiveGuests ? (
        <Form.Check
          className="checkbox"
          type="checkbox"
          label="hide inactive guests"
          id="hideInactiveGuests"
          name="hideInactiveGuests"
          checked={hideInactiveGuests}
          onChange={handleHideInactiveGuestsChange}
        />
      ) : null}
      {setShowCodes ? (
        <Form.Check
          className="checkbox"
          type="checkbox"
          label="show codes"
          id="showCodes"
          name="showCodes"
          checked={showCodes}
          onChange={handleShowCodesChange}
        />
      ) : null}
      <Form.Check
        className="checkbox"
        type="checkbox"
        label="Show ID's"
        id="showIds"
        name="showIds"
        checked={showIds}
        onChange={handleShowIdsChange}
      />
    </div>
  );
}
